import {gql, useQuery} from '@apollo/client';
import {
  ACCOUNT_SEARCH_FRAGMENT,
  ACCOUNT_CONTACT_FRAGMENT,
  CONTACT_SEARCH_FRAGMENT,
  REVIEW,
  ACCOUNT_BASIC_FRAGMENT,
  ACCOUNT_DETAILS_FRAGMENT,
  ACCOUNT_VULNERABILITIES_FRAGMENT,
  VULNERABILITY_FRAGMENT,
} from '../../graphql/fragments';
import {
  GetAccountVulnerabilities,
  GetAccountVulnerabilitiesVariables,
} from '../../schema-types/GetAccountVulnerabilities';
import {useAccountContext} from '../../contexts/AccountContext';
import validateUUID from '../../utils/validateUUID';

export const GET_ACCOUNT_REVIEWS = gql`
  query AccountReviews(
    $accountId: UUID!
    $idType: String!
    $onlyCurrent: Boolean = false
    $curoSync: Boolean = true
    $includeProgressSync: Boolean
  ) {
    reviews(
      accountId: $accountId
      idType: $idType
      onlyCurrent: $onlyCurrent
      curoSync: $curoSync
      includeProgressSync: $includeProgressSync
    ) {
      ...Review
    }
  }
  ${REVIEW}
`;

export const GET_ACCOUNT_BASIC = gql`
  # query AccountDetails($accountId: ID!, $idType: String!, reviewId: String) {
  query SingleAccountBasic($accountId: UUID!, $curoSync: Boolean) {
    accountDetails(accountId: $accountId, curoSync: $curoSync, syncContacts: true) {
      ...AccountBasicFragment
      validationContacts {
        id
        simplifiedName
        dataValidation {
          isValid
          errors {
            field
            message
          }
        }
      }
    }
  }
  ${ACCOUNT_BASIC_FRAGMENT}
`;

export const GET_ACCOUNT_FULL = gql`
  query AccountFull($accountId: UUID!) {
    accountDetails(accountId: $accountId, curoSync: false) {
      ...AccountDetailsFragment
    }
    # active: reviewDetails(keyId: $idType, valueId: $accountId, activeOnly: true) {
    #   id
    #   curoId
    #   name
    # }
  }
  ${ACCOUNT_DETAILS_FRAGMENT}
`;

export const GET_ACCOUNT_VULNERABILITIES = gql`
  query GetAccountVulnerabilities($accountId: UUID!) {
    accountDetails(accountId: $accountId) {
      ...AccountVulnerabilitiesFragment
    }
  }
  ${ACCOUNT_VULNERABILITIES_FRAGMENT}
`;

export const useAccountVulnerabilities = () => {
  const {accountId} = useAccountContext();
  const {data, loading} = useQuery<
    GetAccountVulnerabilities,
    GetAccountVulnerabilitiesVariables
  >(GET_ACCOUNT_VULNERABILITIES, {
    variables: {accountId: validateUUID(accountId)!},
    skip: !accountId,
  });
  return {
    vulnerabilities: data?.accountDetails || null,
    vulnerabilitiesLoading: loading,
  };
};

export const CREATE_REVIEW = gql`
  mutation CreateReview(
    $accountId: UUID!
    $reviewType: String!
    $name: String
    $isSppDa: Boolean
    $ignoreLimit: Boolean = false
  ) {
    createReview(
      accountId: $accountId
      reviewType: $reviewType
      name: $name
      isSppDa: $isSppDa
      ignoreLimit: $ignoreLimit
    ) {
      ok
      errors
      review {
        id
        curoId
        precursorNotFinished
      }
    }
  }
`;

export const DELETE_REVIEW = gql`
  mutation DeleteReview($reviewId: UUID!) {
    deleteReview(reviewId: $reviewId) {
      ok
      reviewId
    }
  }
`;

export const GENERIC_SOFT_DELETE = gql`
  query GenericSoftDeleted($entityType: OVModelsEnum!) {
    genericSoftDeleted(entityType: $entityType) {
      id
      name
      dateDeleted
    }
  }
`;

export const SUBMIT_CONTACT = gql`
  ${CONTACT_SEARCH_FRAGMENT}
  mutation submitContact($contactId: UUID!) {
    submitContact(contactId: $contactId) {
      ok
      contact {
        ...ContactSearchFragment
      }
    }
  }
`;

export const UPDATE_ACCOUNT = gql`
  ${ACCOUNT_CONTACT_FRAGMENT}
  mutation UpdateAccountMutation(
    $accountId: UUID!
    $accountType: String
    $clientType: String
    $curoId: UUID
    $dummyEnum: DummyEnum
    $email: String
    $inHousePlans: Decimal
    $income: Decimal
    $initialFixedFee: Decimal
    $initialPercentageFee: Decimal
    $isDeleted: Boolean
    $liabilities: Decimal
    $name: String
    $netAssets: Decimal
    $ongoingFee: Decimal
    $otherPlans: Decimal
    $plans: Decimal
    $primaryPhone: String
    $propertyAssets: Decimal
    $propositionType: String
    $totalAssets: Decimal
  ) {
    updateAccount(
      accountId: $accountId
      accountType: $accountType
      clientType: $clientType
      curoId: $curoId
      dummyEnum: $dummyEnum
      email: $email
      inHousePlans: $inHousePlans
      income: $income
      initialFixedFee: $initialFixedFee
      initialPercentageFee: $initialPercentageFee
      isDeleted: $isDeleted
      liabilities: $liabilities
      name: $name
      netAssets: $netAssets
      ongoingFee: $ongoingFee
      otherPlans: $otherPlans
      plans: $plans
      primaryPhone: $primaryPhone
      propertyAssets: $propertyAssets
      propositionType: $propositionType
      totalAssets: $totalAssets
    ) {
      ok
      account {
        id
        curoId
        name
        email
        primaryPhone
        income
        netAssets
        propertyAssets
        totalAssets
        inHousePlans
        otherPlans
        plans
        liabilities
        accountType
        clientType
        propositionType
        accountType
        curoId
        email
        inHousePlans
        income
        initialFixedFee
        initialPercentageFee
        isDeleted
        liabilities
        name
        netAssets
        ongoingFee
        otherPlans
        plans
        primaryPhone
        propertyAssets
        totalAssets
        primaryContact {
          ...AccountContact
        }
      }
    }
  }
`;

export const UPDATE_CONTACT = gql`
  ${CONTACT_SEARCH_FRAGMENT}
  ${VULNERABILITY_FRAGMENT}
  mutation UpdateContact(
    $ageVulnerability: Boolean
    $anniversary: DateTime
    $bereavementVulnerability: Boolean
    $contactId: UUID!
    $divorceVulnerability: Boolean
    $dob: DateTime
    $email: String
    $familyStatus: String
    $financialDifficultiesVulnerability: Boolean
    $gender: String
    $genderReassignment: Boolean
    $healthVulnerability: Boolean
    $hearingVulnerability: Boolean
    $inexperienceVulnerability: Boolean
    $informalSalutation: String
    $isDeleted: Boolean
    $jobTitle: String
    $languageVulnerability: Boolean
    $learningVulnerability: Boolean
    $mentalCapacityVulnerability: Boolean
    $mentalHealthVulnerability: Boolean
    $name: String
    $nationality: String
    $otherEventVulnerability: Boolean
    $otherVulnerability: Boolean
    $primaryPhone: String
    $questionnaireStatus: String
    $specialCareApplies: String
    $specialCareReasons: String
    $knownAs: String
    $spouseName: String
    $visionVulnerability: Boolean
    $nationalInsurance: String
    $residency: String
    $domicile: String
    $salutation: String
    $understandsPrivateAssets: Boolean
    $giaHasLiquidityNeeds: Boolean
    $pensionHasLiquidityNeeds: Boolean
    $country: String
  ) {
    updateContact(
      knownAs: $knownAs
      ageVulnerability: $ageVulnerability
      anniversary: $anniversary
      bereavementVulnerability: $bereavementVulnerability
      contactId: $contactId
      divorceVulnerability: $divorceVulnerability
      dob: $dob
      email: $email
      familyStatus: $familyStatus
      financialDifficultiesVulnerability: $financialDifficultiesVulnerability
      gender: $gender
      genderReassignment: $genderReassignment
      healthVulnerability: $healthVulnerability
      hearingVulnerability: $hearingVulnerability
      inexperienceVulnerability: $inexperienceVulnerability
      informalSalutation: $informalSalutation
      isDeleted: $isDeleted
      jobTitle: $jobTitle
      languageVulnerability: $languageVulnerability
      learningVulnerability: $learningVulnerability
      mentalCapacityVulnerability: $mentalCapacityVulnerability
      mentalHealthVulnerability: $mentalHealthVulnerability
      name: $name
      nationality: $nationality
      nationalInsurance: $nationalInsurance
      residency: $residency
      domicile: $domicile
      otherEventVulnerability: $otherEventVulnerability
      otherVulnerability: $otherVulnerability
      primaryPhone: $primaryPhone
      questionnaireStatus: $questionnaireStatus
      specialCareApplies: $specialCareApplies
      specialCareReasons: $specialCareReasons
      spouseName: $spouseName
      salutation: $salutation
      visionVulnerability: $visionVulnerability
      understandsPrivateAssets: $understandsPrivateAssets
      giaHasLiquidityNeeds: $giaHasLiquidityNeeds
      pensionHasLiquidityNeeds: $pensionHasLiquidityNeeds
      country: $country
    ) {
      ok
      contact {
        ...ContactSearchFragment
        ...Vulnerabilities
      }
    }
  }
`;

export const GET_ACCOUNTS = gql`
  query accounts($search: String, $curoIds: [UUID!], $businessUnitIds: [UUID!]) {
    accounts(search: $search, curoIds: $curoIds, businessUnitIds: $businessUnitIds) {
      ...AccountSearchFragment
    }
  }
  ${ACCOUNT_SEARCH_FRAGMENT}
`;

export const LEADS_BASIC_FRAGMENT = gql`
  fragment LeadsBasicFragment on LeadType {
    id
    curoId
    firstName
    lastName
    name
    addresses {
      id
      city
    }
    adviser {
      id
      name
    }
    homePhone
    mobilePhone
    leadType
  }
`;

export const GET_LEADS_AND_ACCOUNTS = gql`
  query LeadsAndAccounts($search: String!) {
    leadsAndAccounts(search: $search) {
      ...LeadsBasicFragment
      ...AccountSearchFragment
    }
  }
  ${ACCOUNT_SEARCH_FRAGMENT}
  ${LEADS_BASIC_FRAGMENT}
`;
